import AdaptiveDialog from "@/lib/components/ui/adaptive-dialog/adaptive-dialog";
import {
  useCallback,
  useEffect,
  useState,
  type ComponentProps,
} from "react";
import { TenantSetupDialogLifecycleContext } from "./tenant-setup-dialog.lifecycle";

export type TenantSetupDialogProps = {
  /** When false, closes an open dialog (e.g. entitlements revoked). */
  wizardEnabled?: boolean;
} & ComponentProps<typeof AdaptiveDialog>;

export function TenantSetupDialog({
  children,
  onOpenChange,
  open,
  wizardEnabled = true,
  ...props
}: TenantSetupDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const resolvedOpen = isControlled ? open : internalOpen;

  const handleOpenChange = useCallback(
    function handleTenantSetupOpenChange(nextOpen: boolean) {
      if (!isControlled) {
        setInternalOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    },
    [isControlled, onOpenChange],
  );

  const closeDialog = useCallback(
    function handleCloseDialog() {
      handleOpenChange(false);
    },
    [handleOpenChange],
  );

  const openDialog = useCallback(
    function handleOpenDialog() {
      handleOpenChange(true);
    },
    [handleOpenChange],
  );

  useEffect(
    function closeWhenWizardDisabled() {
      if (!wizardEnabled && resolvedOpen) {
        const timeoutId = window.setTimeout(function requestCloseWhenDisabled() {
          handleOpenChange(false);
        }, 0);
        return function cancelCloseWhenDisabled() {
          window.clearTimeout(timeoutId);
        };
      }
    },
    [handleOpenChange, resolvedOpen, wizardEnabled],
  );

  const lifecycleValue = {
    closeDialog,
    openDialog,
  };

  return (
    <AdaptiveDialog
      open={resolvedOpen}
      onOpenChange={handleOpenChange}
      {...props}
    >
      <TenantSetupDialogLifecycleContext.Provider value={lifecycleValue}>
        {children}
      </TenantSetupDialogLifecycleContext.Provider>
    </AdaptiveDialog>
  );
}

export { useTenantSetupDialogLifecycleContext } from "./tenant-setup-dialog.lifecycle";
